/**
 * Upload Consumer Photos Actor
 *
 * Uploads photos captured during kiosk check-in flow:
 * 1. Gets consumer photo prompts using getConsumerPhotosToPrompt
 * 2. Converts base64 photos from context to File objects
 * 3. Compresses images
 * 4. Deletes existing photos if replacing
 * 5. Uploads new photos
 * 6. Flushes photos to EHR
 *
 * Supports process_insurance_photos flag to conditionally handle insurance photos.
 * ID photos are always processed.
 */

import {
  deleteConsumerPhoto,
  getConsumerPhotosToPrompt,
  isPhotoExpired,
  uploadConsumerPhoto,
} from "@/app/directories/checkout/components/intake/consumerPhotosHelper";
import { compressImage } from "@/app/directories/checkout/components/intake/intakeHelper";
import { datadogLogs } from "@datadog/browser-logs";
import { flushConsumerPhotosToEhr } from "@frontend/lib/actions/actions";
import {
  InsuranceAccountStatus,
  PhotoType,
} from "@frontend/lib/graphql/generated-gql";
import type { InsuranceData } from "@frontend/lib/schemas/clientSchema";
import { fromPromise } from "xstate";
import { z } from "zod";
import type { ActorWithMetadata } from "./send-sms.actor";

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Retry a promise-returning function with exponential backoff
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  initialDelayMs = 1000,
  maxDelayMs = 10000,
  backoffMultiplier = 2,
): Promise<T> {
  let lastError: unknown;
  let currentDelay = initialDelayMs;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries) {
        await sleep(Math.min(currentDelay, maxDelayMs));
        currentDelay *= backoffMultiplier;
      }
    }
  }

  throw lastError;
}

/**
 * Convert base64 data URL to File object
 */
function base64ToFile(base64String: string, filename: string): File {
  // Extract the base64 data from the string
  const [, data] = base64String.split(",");

  // Convert base64 to binary
  const binaryData = atob(data);

  // Create an array buffer from the binary data
  const arrayBuffer = new ArrayBuffer(binaryData.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < binaryData.length; i++) {
    uint8Array[i] = binaryData.charCodeAt(i);
  }

  // Create a Blob from the array buffer
  const blob = new Blob([arrayBuffer], { type: "image/jpeg" });

  // Create a File object from the Blob
  return new File([blob], filename, { type: "image/jpeg" });
}

/**
 * Map context photo field names to PhotoType
 */
function getPhotoTypeFromFieldName(fieldName: string): PhotoType | null {
  const fieldToTypeMap: Record<string, PhotoType> = {
    licensePhotoFront: PhotoType.IdCardFront,
    passportPhoto: PhotoType.IdCardFront,
    licensePhotoBack: PhotoType.IdCardBack,
    primaryInsurancePhotoFront: PhotoType.InsuranceCardFront,
    primaryInsurancePhotoBack: PhotoType.InsuranceCardBack,
    secondaryInsurancePhotoFront: PhotoType.InsuranceCardFront,
    secondaryInsurancePhotoBack: PhotoType.InsuranceCardBack,
  };

  return fieldToTypeMap[fieldName] || null;
}

/**
 * Get field name from PhotoType and insurance priority
 */
function getFieldNameFromPhotoType(
  photoType: PhotoType,
  insurancePriority?: number,
): string | null {
  if (photoType === PhotoType.IdCardFront) return "licensePhotoFront";
  if (photoType === PhotoType.IdCardBack) return "licensePhotoBack";
  if (photoType === PhotoType.InsuranceCardFront) {
    return insurancePriority === 1
      ? "primaryInsurancePhotoFront"
      : insurancePriority === 2
        ? "secondaryInsurancePhotoFront"
        : null;
  }
  if (photoType === PhotoType.InsuranceCardBack) {
    return insurancePriority === 1
      ? "primaryInsurancePhotoBack"
      : insurancePriority === 2
        ? "secondaryInsurancePhotoBack"
        : null;
  }
  return null;
}

export const SimpleInsuranceDataSchema = z.object({
  payer: z.string(),
  memberId: z.string(),
  state: z.string(),
  priority: z.number().optional(),
  insuranceId: z.string(),
});

export type SimpleInsuranceData = z.infer<typeof SimpleInsuranceDataSchema>;

/**
 * OCR Metadata schema for expiration date tracking
 */
const OcrMetadataSchema = z
  .object({
    expirationDate: z.string().optional().nullable(),
  })
  .passthrough(); // Allow additional fields

/**
 * Existing photo metadata from setup-photos actor (for expiration checking)
 */
const ExistingPhotoMetadataSchema = z.object({
  id: z.string(),
  type: z.string(),
  ocrMetadata: z.record(z.unknown()).nullable().optional(),
  linkedInsuranceId: z.string().nullable().optional(),
  associatedPhotoId: z.string().nullable().optional(),
  insurancePriority: z.number().nullable().optional(),
});

type ExistingPhotoMetadata = z.infer<typeof ExistingPhotoMetadataSchema>;

/**
 * Input Schema (What this actor needs)
 */
const UploadConsumerPhotosInputSchema = z.object({
  consumerId: z.string(),
  orgId: z.number(),
  process_insurance_photos: z.boolean().default(true),
  passportPhoto: z.string().optional(),
  photos: z.object({
    licensePhotoFront: z.string().optional(),
    licensePhotoBack: z.string().optional(),
    primaryInsurancePhotoFront: z.string().optional(),
    primaryInsurancePhotoBack: z.string().optional(),
    secondaryInsurancePhotoFront: z.string().optional(),
    secondaryInsurancePhotoBack: z.string().optional(),
  }),
  // OCR metadata for each photo type (contains expiration dates from NEW captures)
  ocrData: z
    .object({
      ocrId: OcrMetadataSchema.optional(),
      ocrPrimaryInsurance: OcrMetadataSchema.optional(),
      ocrSecondaryInsurance: OcrMetadataSchema.optional(),
    })
    .optional(),
  // Existing photos from setup-photos actor (for expiration checking)
  existingPhotos: z.array(ExistingPhotoMetadataSchema).optional(),
  insurances: z.array(SimpleInsuranceDataSchema).optional(),
  // Retry configuration (optional, with defaults)
  maxRetries: z.number().default(3).optional(),
  retryDelayMs: z.number().default(1000).optional(),
  maxRetryDelayMs: z.number().default(10000).optional(),
});

type UploadConsumerPhotosInput = z.infer<
  typeof UploadConsumerPhotosInputSchema
>;

/**
 * Get OCR metadata for a given photo type and insurance priority
 */
function getOcrMetadataForPhoto(
  ocrData: UploadConsumerPhotosInput["ocrData"],
  photoType: PhotoType,
  insurancePriority?: number,
): Record<string, unknown> | undefined {
  if (!ocrData) return undefined;

  // ID card photos use ocrId
  if (
    photoType === PhotoType.IdCardFront ||
    photoType === PhotoType.IdCardBack
  ) {
    return ocrData.ocrId as Record<string, unknown> | undefined;
  }

  // Insurance card photos use ocrPrimaryInsurance or ocrSecondaryInsurance
  if (
    photoType === PhotoType.InsuranceCardFront ||
    photoType === PhotoType.InsuranceCardBack
  ) {
    if (insurancePriority === 1) {
      return ocrData.ocrPrimaryInsurance as Record<string, unknown> | undefined;
    }
    if (insurancePriority === 2) {
      return ocrData.ocrSecondaryInsurance as
        | Record<string, unknown>
        | undefined;
    }
  }

  return undefined;
}

/**
 * Find existing photo metadata by type and insurance priority
 * Used to check if existing photo has valid (non-expired) ocrMetadata
 */
function findExistingPhotoByType(
  existingPhotos: ExistingPhotoMetadata[] | undefined,
  photoType: PhotoType,
  insurancePriority?: number,
): ExistingPhotoMetadata | undefined {
  if (!existingPhotos || existingPhotos.length === 0) return undefined;

  return existingPhotos.find((photo) => {
    if (photo.type !== photoType) return false;

    // For insurance photos, also match by priority
    if (
      photoType === PhotoType.InsuranceCardFront ||
      photoType === PhotoType.InsuranceCardBack
    ) {
      return photo.insurancePriority === insurancePriority;
    }

    return true;
  });
}

/**
 * Success Schema (What this actor returns on success)
 */
const PhotoUploadResultSchema = z.object({
  photoType: z.string(),
  uploaded: z.boolean(),
  deleted: z.boolean(),
  skipped: z.boolean().optional(), // True if skipped due to valid existing photo
  skipReason: z.string().optional(), // Reason for skipping
  errors: z
    .object({
      uploadError: z.string().optional(),
      deleteError: z.string().optional(),
      compressionError: z.string().optional(),
    })
    .optional(),
});

const UploadConsumerPhotosSuccessSchema = z.object({
  results: z.array(PhotoUploadResultSchema),
  flushed: z.boolean(),
});

type UploadConsumerPhotosSuccess = z.infer<
  typeof UploadConsumerPhotosSuccessSchema
>;

/**
 * Error Schema (What this actor returns on failure)
 */
const UploadConsumerPhotosErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "MISSING_DATA",
    "UPLOAD_ERROR",
    "NETWORK_ERROR",
    "COMPRESSION_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  photoErrors: z
    .array(
      z.object({
        photoType: z.string(),
        error: z.string(),
      }),
    )
    .optional(),
});

type UploadConsumerPhotosError = z.infer<
  typeof UploadConsumerPhotosErrorSchema
>;

/**
 * Actor Implementation
 */
const uploadConsumerPhotosActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: UploadConsumerPhotosInput;
  }): Promise<UploadConsumerPhotosSuccess> => {
    // Validate input
    const validated = UploadConsumerPhotosInputSchema.safeParse(input);

    if (!validated.success) {
      const errorData: UploadConsumerPhotosError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    const {
      consumerId,
      orgId,
      process_insurance_photos,
      photos: rawPhotos,
      passportPhoto,
      ocrData,
      existingPhotos,
      insurances,
      maxRetries = 3,
      retryDelayMs = 1000,
      maxRetryDelayMs = 10000,
    } = validated.data;

    // If passportPhoto is present, treat it as licensePhotoFront (passport is an ID card for the backend)
    const photos = passportPhoto
      ? { ...rawPhotos, licensePhotoFront: passportPhoto }
      : rawPhotos;

    // Check if we have consumerId
    if (!consumerId) {
      const errorData: UploadConsumerPhotosError = {
        code: "MISSING_DATA",
        message: "Consumer ID is required to upload photos",
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    datadogLogs.logger.info(
      `[uploadConsumerPhotosActor] Uploading consumer photos for consumer ${consumerId}`,
      {
        orgId,
        photos,
      },
    );

    try {
      // Convert kiosk insurance format to InsuranceData format
      const insuranceDataArray: InsuranceData[] | undefined = insurances
        ? insurances.map((insurance) => ({
            id: insurance.insuranceId,
            payer: insurance.payer,
            memberId: insurance.memberId,
            state: insurance.state,
            priority: insurance.priority ?? undefined,
            insuranceAccountStatus: InsuranceAccountStatus.Connected, // Default status
            // Optional fields
            consumerId: consumerId,
            isPrimary: insurance.priority === 1,
            isPrimaryInsurance: insurance.priority === 1,
          }))
        : undefined;

      // Get consumer photo prompts
      const photoPrompts = await getConsumerPhotosToPrompt(
        consumerId,
        orgId,
        insuranceDataArray,
      );

      // Filter prompts based on process_insurance_photos flag
      const promptsToProcess = photoPrompts.filter((prompt) => {
        // Always process ID photos
        if (
          prompt.type === PhotoType.IdCardFront ||
          prompt.type === PhotoType.IdCardBack
        ) {
          return true;
        }
        // Only process insurance photos if flag is enabled
        if (process_insurance_photos) {
          return (
            prompt.type === PhotoType.InsuranceCardFront ||
            prompt.type === PhotoType.InsuranceCardBack
          );
        }
        return false;
      });

      // If no prompts to process, return early
      if (promptsToProcess.length === 0) {
        datadogLogs.logger.warn(
          `[uploadConsumerPhotosActor] Consumer ${consumerId} has no photos to upload`,
        );
        // Return in the format expected by saveActorResult (matching onSuccess transformation)
        return {
          results: [],
          flushed: false,
        } as UploadConsumerPhotosSuccess;
      }

      // Process each photo prompt
      const uploadPromises = promptsToProcess.map(async (prompt) => {
        const result: z.infer<typeof PhotoUploadResultSchema> = {
          photoType: prompt.type,
          uploaded: false,
          deleted: false,
        };

        try {
          // Get the field name for this photo type
          const fieldName = getFieldNameFromPhotoType(
            prompt.type,
            prompt.insurancePriority,
          );

          if (!fieldName) {
            result.errors = {
              uploadError: `Unknown photo type: ${prompt.type}`,
            };
            return result;
          }

          // Get base64 photo data from context
          const base64Photo = photos[fieldName as keyof typeof photos] as
            | string
            | undefined;

          // If no photo data, skip (photo not captured)
          if (!base64Photo) {
            return result;
          }

          // Get OCR metadata for this photo type
          const ocrMetadata = getOcrMetadataForPhoto(
            ocrData,
            prompt.type,
            prompt.insurancePriority,
          );

          console.log("Uploading photo with OCR metadata:", ocrMetadata);

          // Check if existing photo has valid (non-expired) expiry date
          // If so, skip upload to prevent duplicate photos
          // Use existingPhotos from setup-photos actor if available (more reliable)
          const existingPhoto = findExistingPhotoByType(
            existingPhotos,
            prompt.type,
            prompt.insurancePriority,
          );

          console.log("Existing photo found:", existingPhoto);

          // Check if existing photo has valid (non-expired) expiry date
          if (existingPhoto?.ocrMetadata) {
            const existingOcrMetadata = existingPhoto.ocrMetadata;
            if (!isPhotoExpired(existingOcrMetadata)) {
              console.log("Existing photo OCR metadata:", existingOcrMetadata);
              result.skipped = true;
              result.skipReason =
                "Existing photo has valid (non-expired) expiration date";
              datadogLogs.logger.info(
                `upload-consumer-photos: skipping ${prompt.type} - existing photo not expired`,
                { consumerId, orgId, photoType: prompt.type },
              );
              return result;
            }
            // Photo is expired, delete it before uploading new one
            if (existingPhoto.id) {
              try {
                await retryWithBackoff(
                  () =>
                    deleteConsumerPhoto(
                      existingPhoto.id,
                      consumerId,
                      orgId,
                    ) as Promise<unknown>,
                  maxRetries,
                  retryDelayMs,
                  maxRetryDelayMs,
                );
                result.deleted = true;
              } catch (error) {
                const errorMessage =
                  error instanceof Error ? error.message : String(error);
                result.errors = {
                  deleteError: errorMessage,
                };
                datadogLogs.logger.warn(
                  `[uploadConsumerPhotosActor] Failed to delete expired photo: ${existingPhoto.id}`,
                  { error: errorMessage, consumerId, orgId },
                );
                // Continue with upload even if delete fails
              }
            }
          }

          // Delete photo from prompt.currentPhoto if it exists
          if (prompt.currentPhoto?.id) {
            const photoId = prompt.currentPhoto.id;
            try {
              await retryWithBackoff(
                () =>
                  deleteConsumerPhoto(
                    photoId,
                    consumerId,
                    orgId,
                  ) as Promise<unknown>,
                maxRetries,
                retryDelayMs,
                maxRetryDelayMs,
              );
              result.deleted = true;
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : String(error);
              result.errors = {
                deleteError: errorMessage,
              };
              datadogLogs.logger.warn(
                `[uploadConsumerPhotosActor] Failed to delete existing photo: ${photoId}`,
                { error: errorMessage, consumerId, orgId },
              );
              // Continue with upload even if delete fails
            }
          }

          // Convert base64 to File
          const filename = `${fieldName}-${Date.now()}.jpg`;
          let photoFile: File;
          try {
            photoFile = base64ToFile(base64Photo, filename);
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            result.errors = {
              ...result.errors,
              uploadError: `Failed to convert base64 to file: ${errorMessage}`,
            };
            return result;
          }

          // Compress image
          let compressedFile: File | null = null;
          try {
            compressedFile = await compressImage(photoFile);
            // If compression fails, check file size and use original if acceptable
            if (!compressedFile) {
              const MAX_FILE_SIZE_MB = 2;
              if (photoFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                result.errors = {
                  ...result.errors,
                  compressionError: "File too large after compression failure",
                };
                return result;
              }
              // Use original file if compression failed but size is acceptable
              compressedFile = photoFile;
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            datadogLogs.logger.warn(
              `[uploadConsumerPhotosActor] Failed to compress image for photo id: ${prompt.currentPhoto?.id}, consumerId: ${consumerId}`,
              {
                error: errorMessage,
              },
            );
            // Continue with original file if compression fails
            compressedFile = photoFile;
          }

          // Upload photo
          const linkedInsuranceId = process_insurance_photos
            ? prompt.currentPhoto?.linkedInsuranceId ||
              prompt.linkToInsuranceId ||
              undefined
            : undefined;

          // Use compressed file or fall back to original
          const fileToUpload = compressedFile || photoFile;

          try {
            await retryWithBackoff(
              () =>
                uploadConsumerPhoto(
                  fileToUpload,
                  consumerId,
                  orgId,
                  prompt.type,
                  true, // isIntake
                  prompt.associatedPhotoId,
                  undefined, // uuid - not using replace operation yet
                  linkedInsuranceId,
                  ocrMetadata, // OCR metadata with expiration date
                ) as Promise<unknown>,
              maxRetries,
              retryDelayMs,
              maxRetryDelayMs,
            );
            result.uploaded = true;
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            result.errors = {
              ...result.errors,
              uploadError: errorMessage,
            };
            datadogLogs.logger.error(
              `[uploadConsumerPhotosActor] Failed to upload photo: ${prompt.type}`,
              {
                error: errorMessage,
                consumerId,
                orgId,
              },
            );
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          result.errors = {
            ...result.errors,
            uploadError: errorMessage,
          };
        }

        return result;
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Flush photos to EHR if any were uploaded
      const anyUploaded = results.some((r) => r.uploaded);
      let flushed = false;
      if (anyUploaded) {
        try {
          await retryWithBackoff(
            () =>
              flushConsumerPhotosToEhr(
                consumerId,
                orgId,
                true,
              ) as Promise<unknown>,
            maxRetries,
            retryDelayMs,
            maxRetryDelayMs,
          );
          flushed = true;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          datadogLogs.logger.error(
            "[uploadConsumerPhotosActor] Failed to flush consumer photos to EHR",
            {
              error: errorMessage,
              consumerId,
              orgId,
            },
          );
          // Don't fail the entire operation if flush fails
        }
      }

      const uploadedCount = results?.filter((r) => r?.uploaded).length ?? 0;
      const errorCount = results?.filter((r) => r?.errors).length ?? 0;
      const totalCount = results?.length ?? 0;
      datadogLogs.logger.info(
        `[uploadConsumerPhotosActor] Finished uploading consumer photos for consumer ${consumerId}`,
        {
          uploadedCount,
          totalCount,
          errorCount,
          flushed,
        },
      );

      return {
        results,
        flushed,
      };
    } catch (error) {
      // Re-throw typed errors
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        "message" in error
      ) {
        throw error;
      }

      // Wrap unknown errors
      const wrappedError: UploadConsumerPhotosError = {
        code: "NETWORK_ERROR",
        message: error instanceof Error ? error.message : "Network error",
        retryable: true,
      };
      const errorObj = new Error(wrappedError.message);
      Object.assign(errorObj, wrappedError);
      throw errorObj;
    }
  },
);

/**
 * Upload Consumer Photos Actor with Metadata
 */
export const uploadConsumerPhotosActor: ActorWithMetadata = {
  impl: uploadConsumerPhotosActorImpl,
  metadata: {
    displayName: "uploadConsumerPhotos",
    description:
      "Upload consumer photos captured during kiosk check-in (ID cards and optionally insurance cards)",
    category: "data",
    retryable: true,
    timeout: 120000, // 120 seconds (multiple uploads + flush)
    errorCodes: [
      "INVALID_INPUT",
      "MISSING_DATA",
      "UPLOAD_ERROR",
      "NETWORK_ERROR",
      "COMPRESSION_ERROR",
    ],
    inputSchema: UploadConsumerPhotosInputSchema,
    successSchema: UploadConsumerPhotosSuccessSchema,
    errorSchema: UploadConsumerPhotosErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as UploadConsumerPhotosSuccess;
      return {
        photoUploadResults: success.results,
        photosFlushed: success.flushed,
      };
    },
    onError: (error: unknown) => {
      const err = error as UploadConsumerPhotosError;
      return {
        photoUploadError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
          photoErrors: err.photoErrors,
        },
      };
    },
  },
};
